import React from "react";

interface PeakScoreProgressProps {
  daysLogged: number;
  daysRequired: number;
  className?: string;
  size?: string;
}

const PeakScoreProgress: React.FC<PeakScoreProgressProps> = ({
  daysLogged,
  daysRequired,
  className = "",
  size = "7rem",
}) => {
  const daysRemaining = Math.max(0, daysRequired - daysLogged);
  const isComplete = daysLogged >= daysRequired;
  const progress = Math.min(100, (daysLogged / daysRequired) * 100);

  // SVG radial config
  const radius = 45;
  const strokeWidth = 6;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className={`flex flex-col items-center space-y-3 ${className}`}>
      {/* Radial Progress */}
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          className="transform -rotate-90"
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
        >
          {/* Inner fill */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="oklch(var(--b1) / 0.4)"
            stroke="none"
          />
          {/* Background track */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="oklch(var(--bc) / 0.1)"
            strokeWidth={strokeWidth}
          />
          {/* Progress arc */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={isComplete ? "#22c55e" : "#0096c7"}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-700 ease-out"
          />
        </svg>
        {/* Center number */}
        <div className="absolute inset-0 flex items-center justify-center">
          {isComplete ? (
            <svg
              className="w-8 h-8 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M5 13l4 4L19 7"
              />
            </svg>
          ) : (
            <span className="text-5xl font-bold font-grotesk text-base-content">
              {daysRemaining}
            </span>
          )}
        </div>
      </div>

      {/* Label */}
      <p className="text-sm font-grotesk text-base-content/70 leading-snug text-center max-w-[200px]">
        {isComplete ? (
          <>
            Peak Readiness Score{" "}
            <span className="text-green-500 font-bold">available</span>
          </>
        ) : (
          <>
            <span className="font-semibold text-base-content">
              {daysRemaining} more day{daysRemaining !== 1 ? "s" : ""}
            </span>{" "}
            to get your <br></br>Peak Readiness Score
          </>
        )}
      </p>
    </div>
  );
};

export default PeakScoreProgress;
